import { TextField } from '@mui/material';
import { useController } from 'react-hook-form';
import NumberFormat from 'react-number-format';

type NumberTextFieldProps = {
  name: `${string}` | `${string}.${string}` | `${string}.${number}`;
  label: string;
  prefix?: string;
  suffix?: string;
  rules?: Object;
  error?: boolean;
  helperText?: string;
  readOnly?: boolean;
  defaultValue?: string | number;
  percent?: boolean;
  fullWidth?: boolean;
  fixedDecimalScale?: boolean;
  decimalScale?: number;
};

export function NumberTextField({
  name,
  label,
  prefix,
  suffix,
  rules,
  error,
  helperText,
  readOnly,
  defaultValue,
  percent,
  fullWidth = true,
  fixedDecimalScale,
  decimalScale = 2,
}: NumberTextFieldProps) {
  const {
    field: { ref, onChange, value },
  } = useController({
    name,
    rules,
    defaultValue,
  });
  if (percent) {
    suffix = '%';
  }

  const setFixedValue = (n: number | string): string => {
    return Number(n).toFixed(percent ? 5 : 2);
  };

  const fieldValue = value === null ? '' : percent ? setFixedValue(value * 100) : value;

  return (
    <NumberFormat
      id={name}
      prefix={prefix}
      suffix={suffix}
      thousandSeparator={true}
      customInput={TextField}
      onBlur={() => {
        if (fieldValue === '') {
          const newValue = percent ? 0.0 : 0.0;
          onChange(newValue);
          return;
        }

        const newValue = percent ? fieldValue / 100 : fieldValue;
        onChange(setFixedValue(newValue));
      }}
      onValueChange={v => {
        if (v.value === '') {
          onChange(null);
          return;
        }

        const value = Number(v.floatValue || v.value);
        const newValue = percent && value ? value / 100 : value;
        onChange(setFixedValue(newValue));
      }}
      value={fieldValue}
      fullWidth={fullWidth}
      decimalScale={decimalScale}
      fixedDecimalScale={fixedDecimalScale}
      label={label}
      error={error}
      helperText={helperText}
      variant='outlined'
      readOnly={readOnly}
      getInputRef={ref}
    />
  );
}
